import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import API from '../../services/api';
import { 
  FiCheck, 
  FiCalendar, 
  FiUser, 
  FiAlertCircle,
  FiChevronDown,
  FiChevronUp,
  FiFilter,
  FiSearch
} from 'react-icons/fi';
import { format, parseISO, isBefore } from 'date-fns';

const TechnicianTaskView = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedTask, setExpandedTask] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'completed'
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchTechnicianTasks = async () => {
      try {
        const res = await API.get(`/tasks/technician/${user._id}`);
        setTasks(res.data.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load tasks');
      } finally {
        setLoading(false);
      }
    };
    
    if (user?._id) {
      fetchTechnicianTasks();
    }
  }, [user]);

  const handleTaskStatusUpdate = async (taskId, newStatus) => {
    try {
      const res = await API.patch(`/tasks/${taskId}/status`, {
        status: newStatus,
        updatedBy: user._id
      });
      
      if (res.data.success) {
        setTasks(prev => prev.map(task => 
          task._id === taskId ? { ...task, completed: newStatus } : task
        ));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update task');
    }
  };

  const toggleTaskExpansion = (taskId) => {
    setExpandedTask(expandedTask === taskId ? null : taskId);
  };

  const filteredTasks = tasks.filter(task => {
    // Filter by status
    const statusMatch = 
      filter === 'all' || 
      (filter === 'pending' && !task.completed) || 
      (filter === 'completed' && task.completed);
    
    // Filter by search term
    const searchMatch = 
      task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    return statusMatch && searchMatch;
  });

  const getTaskPriority = (task) => {
    if (!task.dueDate) return 'low';
    const dueDate = parseISO(task.dueDate);
    const today = new Date();
    
    if (isBefore(dueDate, today)) return 'high';
    if (isBefore(dueDate, new Date(today.setDate(today.getDate() + 3)))) return 'medium';
    return 'low';
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger d-flex align-items-center">
          <FiAlertCircle className="me-2" size={20} />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">My Tasks</h2>
        <div className="d-flex gap-2">
          <div className="input-group" style={{ width: '250px' }}>
            <span className="input-group-text bg-white">
              <FiSearch />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="dropdown">
            <button
              className="btn btn-outline-secondary dropdown-toggle d-flex align-items-center"
              type="button"
              id="filterDropdown"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <FiFilter className="me-2" />
              {filter === 'all' ? 'All Tasks' : filter === 'pending' ? 'Pending' : 'Completed'}
            </button>
            <ul className="dropdown-menu" aria-labelledby="filterDropdown">
              <li>
                <button 
                  className="dropdown-item" 
                  onClick={() => setFilter('all')}
                >
                  All Tasks
                </button>
              </li>
              <li>
                <button 
                  className="dropdown-item" 
                  onClick={() => setFilter('pending')}
                >
                  Pending
                </button>
              </li>
              <li>
                <button 
                  className="dropdown-item" 
                  onClick={() => setFilter('completed')}
                >
                  Completed
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
      
      {filteredTasks.length === 0 ? (
        <div className="card shadow-sm">
          <div className="card-body text-center py-5">
            <FiUser size={48} className="text-muted mb-3" />
            <h5 className="text-muted">No tasks found</h5>
            <p className="text-muted">
              {searchTerm 
                ? "Try a different search term" 
                : filter === 'completed' 
                  ? "You haven't completed any tasks yet" 
                  : "You don't have any pending tasks"}
            </p>
          </div>
        </div>
      ) : (
        <div className="row g-4">
          {filteredTasks.map(task => {
            const priority = getTaskPriority(task);
            const isExpanded = expandedTask === task._id;
            
            return (
              <div key={task._id} className="col-md-6 col-lg-4">
                <div className={`card h-100 shadow-sm border-${priority === 'high' ? 'danger' : priority === 'medium' ? 'warning' : 'success'}`}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <h5 className="card-title mb-0">{task.title}</h5>
                      <span 
                        className={`badge rounded-pill bg-${task.completed ? 'success' : priority === 'high' ? 'danger' : priority === 'medium' ? 'warning' : 'secondary'}`}
                      >
                        {task.completed ? 'Completed' : priority === 'high' ? 'Urgent' : priority === 'medium' ? 'Due Soon' : 'Low Priority'}
                      </span>
                    </div>
                    
                    <div className="d-flex align-items-center text-muted mb-3">
                      <FiCalendar className="me-2" />
                      <small>
                        Due: {task.dueDate ? format(parseISO(task.dueDate), 'MMM dd, yyyy') : 'No deadline'}
                      </small>
                    </div>
                    
                    {isExpanded && (
                      <div className="mb-3">
                        <p className="card-text">{task.description}</p>
                        <div className="text-muted small">
                          <div>Team: {task.team?.name || 'Unassigned'}</div>
                          <div>Assigned by: {task.createdBy?.name || 'Unknown'}</div>
                        </div>
                      </div>
                    )}
                    
                    <div className="d-flex justify-content-between align-items-center">
                      <button 
                        className="btn btn-sm btn-link p-0"
                        onClick={() => toggleTaskExpansion(task._id)}
                      >
                        {isExpanded ? (
                          <>
                            <FiChevronUp className="me-1" /> Show Less
                          </>
                        ) : (
                          <>
                            <FiChevronDown className="me-1" /> Show More
                          </>
                        )}
                      </button>
                      
                      {!task.completed && (
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleTaskStatusUpdate(task._id, true)}
                        >
                          <FiCheck className="me-1" /> Complete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TechnicianTaskView;